import React from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { AppLayout } from '../../layouts/AppLayout'
import { PageHeader } from '../../layouts/PageHeader'
import { useProjectLineageMeta } from '../../../hooks/use-project-lineage-meta'
import { ProjectDetailPeriodControls } from './project-detail-period-controls'
import { ProjectDetailHeaderBar } from './project-detail-header-bar'
import { appPath } from '../../../utils/appPath'

const backBtnClass =
  'flex items-center gap-1.5 text-[12px] font-medium text-slate-600 border border-slate-200 rounded-lg px-2.5 py-1 hover:bg-white hover:text-slate-900 transition-colors flex-none shrink-0 bg-white/80'

export function ProjectDetailInvalidId() {
  return (
    <AppLayout>
      <PageHeader>
        <div className="flex items-center gap-3 min-w-0">
          <Link to={appPath('/project-analysis')} className={backBtnClass}>
            <ArrowLeft size={13} /> Back
          </Link>
          <h1 className="text-[15px] font-semibold text-[#0D0D0D] truncate min-w-0">
            Project detail
          </h1>
        </div>
      </PageHeader>
      <div className="p-6">
        <p className="text-sm text-slate-600">Invalid project ID.</p>
        <Link
          to={appPath('/project-analysis')}
          className="text-sm text-blue-600 font-medium mt-2 inline-block hover:text-blue-800"
        >
          ← Back to overview
        </Link>
      </div>
    </AppLayout>
  )
}

export function ProjectDetailLoading() {
  const { projectId: raw } = useParams<{ projectId: string }>()
  const id = raw ? parseInt(raw, 10) : NaN
  const label =
    Number.isFinite(id) ? `Loading project ${id}…` : 'Loading project…'

  return (
    <AppLayout>
      <PageHeader>
        <div className="flex items-center gap-3 min-w-0">
          <Link to={appPath('/project-analysis')} className={backBtnClass}>
            <ArrowLeft size={13} /> Back
          </Link>
          <h1 className="text-[15px] font-semibold text-[#0D0D0D] truncate min-w-0">
            {label}
          </h1>
        </div>
      </PageHeader>
      <div className="p-6 flex items-center justify-center py-24">
        <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
        <span className="ml-3 text-slate-500 text-sm">Loading project data…</span>
      </div>
    </AppLayout>
  )
}

export function ProjectDetailFetchError({ message }: { message: string }) {
  return (
    <AppLayout>
      <PageHeader>
        <div className="flex items-center gap-3 min-w-0">
          <Link to={appPath('/project-analysis')} className={backBtnClass}>
            <ArrowLeft size={13} /> Back
          </Link>
          <h1 className="text-[15px] font-semibold text-[#0D0D0D] truncate min-w-0">
            Project detail
          </h1>
        </div>
      </PageHeader>
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center max-w-lg">
          <p className="text-red-700 text-sm font-medium">Failed to load project data</p>
          <p className="text-red-500 text-xs mt-1">{message}</p>
          <Link
            to={appPath('/project-analysis')}
            className="mt-4 inline-block text-[12px] font-medium text-blue-600 hover:text-blue-800"
          >
            ← Back to list
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}

export interface ProjectDetailPeriodToolbarProps {
  detailMode: 'fy' | 'calendar'
  onDetailModeChange: (m: 'fy' | 'calendar') => void
  detailFY: string
  onDetailFYChange: (v: string) => void
  detailQ: string
  onDetailQChange: (v: string) => void
  detailYear: string
  onDetailYearChange: (v: string) => void
  detailMonth: string
  onDetailMonthChange: (v: string) => void
  detailFYOptions: { value: string; label: string }[]
  detailYearOptions: number[]
}

export interface ProjectDetailEmptyPeriodProps extends ProjectDetailPeriodToolbarProps {
  pageTitle: string
  projectId: number
}

export function ProjectDetailEmptyPeriod({
  pageTitle,
  projectId,
  ...periodProps
}: ProjectDetailEmptyPeriodProps) {
  const lineage = useProjectLineageMeta(
    Number.isFinite(projectId) ? projectId : null,
  )
  const periodSlot = (
    <ProjectDetailPeriodControls
      {...periodProps}
      className="w-full lg:w-auto justify-end"
    />
  )

  return (
    <AppLayout>
      <PageHeader>
        <ProjectDetailHeaderBar
          title={pageTitle}
          category={null}
          lineageMeta={{
            accountManager: lineage.accountManager,
            clientName: lineage.clientName,
            projectManager: lineage.projectManager,
          }}
          lineageMetaLoading={lineage.loading}
          statusPill="No rows for this period"
          periodSlot={periodSlot}
        />
      </PageHeader>
      <div className="p-6 space-y-3">
        <p className="text-sm text-slate-600">
          Adjust FY or calendar filters in the header, or go back to the list.
        </p>
      </div>
    </AppLayout>
  )
}

export interface ProjectDetailNoDataProps extends ProjectDetailPeriodToolbarProps {
  projectId: number
}

export function ProjectDetailNoData({
  projectId,
  ...periodProps
}: ProjectDetailNoDataProps) {
  const lineage = useProjectLineageMeta(
    Number.isFinite(projectId) ? projectId : null,
  )
  const title = Number.isFinite(projectId)
    ? `Project ${projectId}`
    : 'Project detail'

  const periodSlot = (
    <ProjectDetailPeriodControls
      {...periodProps}
      className="w-full lg:w-auto justify-end"
    />
  )

  return (
    <AppLayout>
      <PageHeader>
        <ProjectDetailHeaderBar
          title={title}
          category={null}
          lineageMeta={{
            accountManager: lineage.accountManager,
            clientName: lineage.clientName,
            projectManager: lineage.projectManager,
          }}
          lineageMetaLoading={lineage.loading}
          statusPill="No timesheet data"
          periodSlot={periodSlot}
        />
      </PageHeader>
      <div className="p-6 space-y-3">
        <p className="text-sm text-slate-600">
          There is no gold-fact data for this ID. Check the project or regenerate
          aggregates.
        </p>
      </div>
    </AppLayout>
  )
}
